// Angular
import { Injectable } from '@angular/core';
import nacl from 'tweetnacl';

// Models
import { SessionStorageKeyEnum } from './session-storage';
import {
  BookingDataI,
  BookingI,
  CustomerI,
  OrderTotalsI,
  ProductI,
  PromoCodeI,
  ReschBookingDataI,
  ServicesI,
  SignupFormI,
  SlotNI,
  TestingEventI,
} from '@bodyanalytics/data-models-ui';

// Marks a stored value as ciphertext produced by encryptValue() below, so reads
// can tell encrypted payloads apart from legacy plaintext written before this
// change (and decode each accordingly).
const ENCRYPTED_PREFIX = 'enc.v1.';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  private readonly sessionStorageEnum = SessionStorageKeyEnum;
  public readonly bookingReservationKey = this.sessionStorageEnum.BOOKING_TOKEN;
  public readonly rescheduleBookingReservationKey = this.sessionStorageEnum.RESCHEDULE_TOKEN;

  // ── At-rest encryption for booking PII ────────────────────────────────────
  // The booking/reschedule blobs carry customer PII (name, email, phone,
  // address, DOB). Stored as plaintext JSON they were readable in the browser's
  // Application → Session Storage panel (DevTools flagged the token as
  // unencrypted). We now encrypt them with NaCl secretbox (XSalsa20-Poly1305)
  // under a per-tab random key before writing, and decrypt on read — the public
  // API stays synchronous, so callers are unchanged.
  //
  // Scope/limits: this is at-rest obfuscation, NOT a trust boundary. The key
  // lives in the same per-tab sessionStorage, because any secret shipped to the
  // browser is reachable by code running in the page. It removes plaintext PII
  // from storage and defends against casual inspection, screenshots and shared
  // screens. The durable fixes — storing less PII client-side and/or an opaque
  // server-side reservation token — are tracked as follow-ups.

  private getEncryptionKey(): Uint8Array {
    const existing = sessionStorage.getItem(this.sessionStorageEnum.BOOKING_SESSION_KEY);
    if (existing) {
      try {
        const bytes = base64ToBytes(existing);
        if (bytes.length === nacl.secretbox.keyLength) {
          return bytes;
        }
      } catch {
        // Corrupt key material — fall through and mint a fresh key.
      }
    }
    const key = nacl.randomBytes(nacl.secretbox.keyLength);
    sessionStorage.setItem(this.sessionStorageEnum.BOOKING_SESSION_KEY, bytesToBase64(key));
    return key;
  }

  private encryptValue<T>(value: T): string {
    const key = this.getEncryptionKey();
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
    const message = new TextEncoder().encode(JSON.stringify(value));
    const box = nacl.secretbox(message, nonce, key);
    const combined = new Uint8Array(nonce.length + box.length);
    combined.set(nonce, 0);
    combined.set(box, nonce.length);
    return ENCRYPTED_PREFIX + bytesToBase64(combined);
  }

  private decryptValue<T>(raw: string): T | null {
    if (raw.startsWith(ENCRYPTED_PREFIX)) {
      try {
        const combined = base64ToBytes(raw.slice(ENCRYPTED_PREFIX.length));
        const nonce = combined.slice(0, nacl.secretbox.nonceLength);
        const box = combined.slice(nacl.secretbox.nonceLength);
        const opened = nacl.secretbox.open(box, nonce, this.getEncryptionKey());
        if (!opened) {
          return null;
        }
        return JSON.parse(new TextDecoder().decode(opened)) as T;
      } catch {
        // Tampered/corrupt ciphertext, or a key from a different tab — never
        // throw into the funnel; treat as "no usable data".
        return null;
      }
    }
    // Backward-compat: a token written as plaintext before this change (e.g. an
    // in-flight booking spanning the deploy). Parse it so the funnel survives.
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  private setEncryptedItem<T>(key: string, value: T): void {
    sessionStorage.setItem(key, this.encryptValue(value));
  }

  private getEncryptedItem<T>(key: string): T | null {
    const raw = sessionStorage.getItem(key);
    return raw ? this.decryptValue<T>(raw) : null;
  }

  saveRescheduleBookingData(
    existingBookingData: BookingI | null,
    existingOrderId: string | null,
    newEvent: TestingEventI | null,
    newTimeSlot: Date | null,
  ) {
    const bookingData: ReschBookingDataI = {
      // selectedTimeSlot: newTimeSlot,
      existingBooking: existingBookingData,
      existingOrderId: existingOrderId,
      selectedEvent: newEvent,
      selectedTimeSlot: newTimeSlot,
    };

    this.setEncryptedItem(this.rescheduleBookingReservationKey, bookingData);
  }

  getRescheduleFlag(): boolean {
    const dataJson = sessionStorage.getItem(this.rescheduleBookingReservationKey);
    return dataJson ? true : false;
  }

  getRescheduleBookingData(): ReschBookingDataI | null {
    return this.getEncryptedItem<ReschBookingDataI>(this.rescheduleBookingReservationKey);
  }

  clearRescheduleBookingData(): void {
    sessionStorage.removeItem(this.rescheduleBookingReservationKey);
  }

  saveBookingData(
    timeSlot: Date | null,
    product: ProductI,
    service: ServicesI | null,
    userInfo: SignupFormI | null,
    bookingTotalWithTaxServiceFee: number,
    selectedSlot: SlotNI | null,
    selectedEvent: TestingEventI | null,
    customerData: CustomerI | null,
    orderTotal: OrderTotalsI | null,
    promotion: PromoCodeI | null,
    workflowStep: string,
  ): void {
    const BookingDataI: BookingDataI = {
      selectedTime: timeSlot,
      selectedProduct: product,
      selectedService: service,
      userInfo: userInfo,
      bookingTotalWithTaxServiceFee: bookingTotalWithTaxServiceFee,
      selectedSlot: selectedSlot,
      selectedEvent: selectedEvent,
      savedCustomerData: customerData,
      selectedTimeSlot: null,
      orderTotal: orderTotal,
      promotion: promotion,
      workflowStep: workflowStep,
    };
    this.setEncryptedItem(this.bookingReservationKey, BookingDataI);
  }

  getBookingData(): BookingDataI | null {
    return this.getEncryptedItem<BookingDataI>(this.bookingReservationKey);
  }

  // ── #2157 round 2 — PaymentIntent persistence across refreshes ─────────────
  // Every PaymentIntent creation claims a promo redemption server-side, so a
  // plain checkout-page refresh used to mint a new PI AND burn a redemption —
  // a single-use make-good credit could be exhausted with zero charges made.
  // The minted PI is stored (encrypted, same at-rest scheme as the booking
  // blob — the client_secret is sensitive) keyed to cart + promo identity;
  // the checkout page reuses it while the identity matches.

  savePaymentIntentSession(ctx: PaymentIntentSessionI): void {
    this.setEncryptedItem(this.sessionStorageEnum.PAYMENT_INTENT_TOKEN, ctx);
  }

  getPaymentIntentSession(): PaymentIntentSessionI | null {
    return this.getEncryptedItem<PaymentIntentSessionI>(this.sessionStorageEnum.PAYMENT_INTENT_TOKEN);
  }

  clearPaymentIntentSession(): void {
    sessionStorage.removeItem(this.sessionStorageEnum.PAYMENT_INTENT_TOKEN);
  }

  clearBookingData(): void {
    sessionStorage.removeItem(this.bookingReservationKey);
    // The PI belongs to the booking session it was minted for — never let it
    // outlive the booking data and be reused for a future cart.
    this.clearPaymentIntentSession();
  }
  public clear(): void {
    sessionStorage.clear();
  }
}

/**
 * The PaymentIntent minted for the current checkout, keyed to the cart + promo
 * identity it was created against (#2157 round 2). `promocode` is part of the
 * identity: applying/removing a promo requires a NEW PaymentIntent (different
 * amount + different server-side redemption claim).
 */
export interface PaymentIntentSessionI {
  paymentIntentId: string;
  clientSecret: string;
  amount: number;
  status: string;
  productId: string;
  eventId: string;
  promocode: string;
}
